/**
 * MFA Enrollment Component
 * SOC 2 Controls: CC6.1, CC6.2
 * ISO 27001: A.9.4.2
 *
 * Allows users to enroll in TOTP-based multi-factor authentication.
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Shield, Smartphone, Copy, Check, Loader2 } from 'lucide-react';

interface MFAEnrollmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEnrollmentComplete?: () => void;
}

type EnrollmentStep = 'intro' | 'qr' | 'verify' | 'backup' | 'complete';

export default function MFAEnrollment({
  open,
  onOpenChange,
  onEnrollmentComplete,
}: MFAEnrollmentProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<EnrollmentStep>('intro');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // MFA enrollment state
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const resetState = () => {
    setStep('intro');
    setFactorId(null);
    setQrCode(null);
    setSecret(null);
    setVerifyCode('');
    setBackupCodes([]);
    setError(null);
  };

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  // Step 1: Start enrollment - get QR code
  const startEnrollment = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (enrollError) throw enrollError;

      if (data) {
        setFactorId(data.id);
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
        setStep('qr');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start MFA enrollment');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify the TOTP code
  const verifyEnrollment = async () => {
    if (!factorId || verifyCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      // Generate backup codes using cryptographically secure random values
      const codes = Array.from({ length: 10 }, () => {
        const bytes = new Uint8Array(4);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
      });
      setBackupCodes(codes);
      setStep('backup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  // Copy secret to clipboard
  const copySecret = async () => {
    if (secret) {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Copy backup codes
  const copyBackupCodes = async () => {
    await navigator.clipboard.writeText(backupCodes.join('\n'));
    toast({
      title: 'Copied!',
      description: 'Backup codes copied to clipboard',
    });
  };

  // Complete enrollment
  const completeEnrollment = () => {
    toast({
      title: 'MFA Enabled',
      description: 'Two-factor authentication is now active on your account.',
    });
    onEnrollmentComplete?.();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {step === 'complete' ? 'MFA Enabled!' : 'Set Up Two-Factor Authentication'}
          </DialogTitle>
          <DialogDescription>
            {step === 'intro' && 'Add an extra layer of security to your account'}
            {step === 'qr' && 'Scan this QR code with your authenticator app'}
            {step === 'verify' && 'Enter the code from your authenticator app'}
            {step === 'backup' && 'Save your backup codes in a safe place'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step: Introduction */}
        {step === 'intro' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <Smartphone className="h-5 w-5 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">You'll need an authenticator app</p>
                <p className="text-sm text-muted-foreground">
                  We recommend Google Authenticator, Authy, or 1Password.
                </p>
              </div>
            </div>
            <Button onClick={startEnrollment} disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                'Get Started'
              )}
            </Button>
          </div>
        )}

        {/* Step: QR Code */}
        {step === 'qr' && qrCode && (
          <div className="space-y-4">
            <div className="flex justify-center p-4 bg-white rounded-lg">
              <img src={qrCode} alt="QR Code for MFA" className="w-48 h-48" />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                Can't scan? Enter this code manually:
              </Label>
              <div className="flex gap-2">
                <Input
                  value={secret || ''}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={copySecret}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button onClick={() => setStep('verify')} className="w-full">
              I've scanned the code
            </Button>
          </div>
        )}

        {/* Step: Verify */}
        {step === 'verify' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="verify-code">Enter 6-digit code</Label>
              <Input
                id="verify-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('qr')} className="flex-1">
                Back
              </Button>
              <Button
                onClick={verifyEnrollment}
                disabled={loading || verifyCode.length !== 6}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Backup Codes */}
        {step === 'backup' && (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                Save these backup codes securely. Each code can only be used once if you
                lose access to your authenticator app.
              </AlertDescription>
            </Alert>

            <Card>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="p-2 bg-muted rounded text-center">
                      {code}
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" onClick={copyBackupCodes} className="w-full">
                  <Copy className="h-4 w-4" />
                  Copy Codes
                </Button>
              </CardFooter>
            </Card>

            <Button onClick={completeEnrollment} className="w-full">
              I've saved my backup codes
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
